import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Search, Download, MoreHorizontal, Pencil, Archive, Loader2, FileDown, Trash2 } from "lucide-react";
import { RESOURCES } from "@/config/resources";
import { StatusChip } from "@/components/StatusChip";
import { PageHeader, EmptyState } from "@/components/Primitives";
import EntityDialog from "@/components/EntityDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatErr, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAttention } from "@/context/AttentionContext";

async function loadSources(keys) {
  const out = {};
  const map = {
    users: ["/users", (d) => d.items.map((u) => ({ value: u.id, label: u.name }))],
    territories: ["/territories", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
    zones: ["/zones", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
    zone_options: ["/mz/zone-options", (d) => d.items.map((z) => ({ value: z.id, label: `${z.market} › ${z.name}` }))],
    lead_statuses: ["/lead_statuses", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
    lead_sources: ["/lead_sources", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
    pipeline_stages: ["/pipeline_stages", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
    products: ["/products", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
    businesses: ["/businesses", (d) => d.items.map((t) => ({ value: t.id, label: t.name }))],
  };
  await Promise.all(keys.map(async (k) => {
    if (!map[k]) return;
    try { const { data } = await api.get(map[k][0]); out[k] = map[k][1](data); } catch { out[k] = []; }
  }));
  return out;
}

export default function CrudResource({ resourceKey }) {
  const cfg = RESOURCES[resourceKey];
  const { can, user } = useAuth();
  const attention = useAttention();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [sources, setSources] = useState({});
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  // Config/reference resources are admin-write only; managers & reps get a
  // read-only view (matches backend ADMIN_ROLES write enforcement).
  const ADMIN_WRITE = ["territories", "zones", "products", "commission_rules",
    "lead_sources", "lead_statuses", "pipeline_stages", "custom_fields"];
  const isAdmin = can("tenant_owner", "platform_owner");
  const canWrite = user?.role !== "readonly" &&
    (!ADMIN_WRITE.includes(resourceKey) && !ADMIN_WRITE.includes(cfg.endpoint) ? true : isAdmin);
  // Sales Representatives (and read-only) can never delete/archive records (matches backend rule).
  const canDelete = !["sales_rep", "readonly"].includes(user?.role);
  // Bulk selection + permanent delete (shared across all CRUD lists).
  const SELECTABLE = ["leads", "businesses", "contacts", "opportunities", "advertisers", "events", "contracts"];
  const PURGE_PROTECTED_FE = ["contracts", "commissions"];
  const showSelect = canDelete && SELECTABLE.includes(cfg.endpoint);
  const canPurge = canDelete && !PURGE_PROTECTED_FE.includes(cfg.endpoint);
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const rowLabel = (row) => row[cfg.columns[0]?.key] || row.name || row.business_name || "this record";

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (q) sp.set("q", q);
      Object.entries(filters).forEach(([k, v]) => v && v !== "__all" && sp.set(k, v));
      const { data } = await api.get(`/${cfg.endpoint}?${sp.toString()}`);
      setRows(data.items);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [cfg.endpoint, q, filters]);

  useEffect(() => {
    const need = new Set();
    cfg.fields?.forEach((f) => f.optionsSource && need.add(f.optionsSource));
    cfg.filters?.forEach((f) => f.optionsSource && need.add(f.optionsSource));
    loadSources([...need]).then(setSources);
  }, [resourceKey]);

  useEffect(() => { const t = setTimeout(fetchRows, 250); return () => clearTimeout(t); }, [fetchRows]);

  useEffect(() => {
    if (params.get("new") === "1") { setDialog({ open: true, initial: null }); setParams({}, { replace: true }); }
  }, [params]);

  // Open a specific record's edit dialog via ?open=<id> (e.g. after Convert to Lead).
  useEffect(() => {
    const openId = params.get("open");
    if (openId && rows.some((r) => r.id === openId)) {
      setDialog({ open: true, initial: rows.find((r) => r.id === openId) });
      setParams({}, { replace: true });
    }
  }, [params, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRowClick = (row) => {
    if (cfg.rowLink && resourceKey === "businesses") navigate(`/businesses/${row.id}`);
    else if (canWrite) {
      setDialog({ open: true, initial: row });
      // Viewing a specific lead acknowledges it for THIS user (clears their badge),
      // without changing the lead's sales status.
      if (resourceKey === "leads") {
        api.post(`/leads/${row.id}/acknowledge`).then(() => attention.refresh()).catch(() => {});
      }
    }
  };

  // Keep selection reconciled with what's visible (filter/search/pagination safe).
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(rows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const runConfirm = async () => {
    const { mode, ids } = confirm;
    const path = mode === "purge" ? `/${cfg.endpoint}/bulk-delete` : `/${cfg.endpoint}/bulk-archive`;
    try {
      const { data } = await api.post(path, { ids });
      const done = mode === "purge" ? data.deleted : data.archived;
      const failed = data.failed || [];
      const verb = mode === "purge" ? "deleted" : "archived";
      if (failed.length) toast.warning(`${done} ${verb}. ${failed.length} could not be ${verb}${failed[0]?.reason ? `: ${failed[0].reason}` : ""}.`);
      else toast.success(`${done} ${cfg.singular.toLowerCase()}${done === 1 ? "" : "s"} ${verb}.`);
      setConfirm(null); setSelected(new Set()); fetchRows(); attention.refresh();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`/export/${cfg.endpoint}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = `${cfg.endpoint}.csv`; a.click();
    } catch { toast.error("Export not available"); }
  };

  const renderCell = (c, row) => (
    c.chip ? <StatusChip value={row[c.key]} />
      : c.render ? <span className={c.muted ? "text-slate-500" : ""}>{c.render(row[c.key], row)}</span>
        : <span className={c.bold ? "font-semibold text-slate-800" : c.muted ? "text-slate-500" : "text-slate-600"}>{row[c.key] || "—"}</span>
  );

  const rowMenu = (row) => (
    canWrite ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="h-8 w-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" data-testid={`row-menu-${row.id}`}><MoreHorizontal className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {cfg.pdf && <DropdownMenuItem onClick={() => { const p = cfg.pdf(row); downloadBlob(p.path, p.filename); }} data-testid={`pdf-${row.id}`}><FileDown className="h-4 w-4 mr-2" />Download PDF</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => setDialog({ open: true, initial: row })}><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
          {canDelete && <DropdownMenuItem onClick={() => setConfirm({ mode: "archive", ids: [row.id], label: rowLabel(row) })}><Archive className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>}
          {canPurge && <DropdownMenuItem onClick={() => setConfirm({ mode: "purge", ids: [row.id], label: rowLabel(row) })} className="text-[#C42607]" data-testid={`delete-${row.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete Permanently</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null
  );

  const emptyBlock = (
    <EmptyState icon={cfg.icon} title={`No ${cfg.title.toLowerCase()} yet`} hint={`Create your first ${cfg.singular.toLowerCase()} to get started.`}
      action={canWrite && <Button size="sm" onClick={() => setDialog({ open: true, initial: null })}><Plus className="h-4 w-4" />New {cfg.singular}</Button>} />
  );

  return (
    <div data-testid={`page-${resourceKey}`}>
      <PageHeader title={cfg.title} subtitle={`${rows.length} record${rows.length === 1 ? "" : "s"}`}>
        {["leads", "businesses", "contacts", "contracts", "commissions"].includes(cfg.endpoint) && (
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-btn"><Download className="h-4 w-4" />Export</Button>
        )}
        {canWrite && (
          <Button size="sm" onClick={() => setDialog({ open: true, initial: null })} data-testid="create-btn">
            <Plus className="h-4 w-4" />New {cfg.singular}
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder={`Search ${cfg.title.toLowerCase()}…`} className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
        </div>
        {cfg.filters?.map((f) => {
          const opts = f.optionsSource ? (sources[f.optionsSource] || []) : (f.options || []).map((o) => ({ value: o, label: o }));
          return (
            <Select key={f.name} value={filters[f.name] || "__all"} onValueChange={(v) => setFilters((p) => ({ ...p, [f.name]: v }))}>
              <SelectTrigger className="w-40" data-testid={`filter-${f.name}`}><SelectValue placeholder={f.label} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All {f.label}</SelectItem>
                {opts.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          );
        })}
      </div>

      {showSelect && someSelected && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#00C9E0]/30 bg-[#00C9E0]/5 px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-slate-700" data-testid="bulk-count">{selected.size} selected</span>
          <div className="flex-1" />
          {canDelete && <Button variant="outline" size="sm" onClick={() => setConfirm({ mode: "archive", ids: [...selected] })} data-testid="bulk-archive"><Archive className="h-4 w-4" />Archive</Button>}
          {canPurge && <Button variant="outline" size="sm" className="border-[#C42607]/40 text-[#C42607] hover:bg-[#C42607]/10" onClick={() => setConfirm({ mode: "purge", ids: [...selected] })} data-testid="bulk-delete"><Trash2 className="h-4 w-4" />Delete Permanently</Button>}
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear Selection</Button>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block cc-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="data-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70">
                {showSelect && (
                  <th className="w-10 px-4 py-3"><Checkbox checked={allSelected ? true : someSelected ? "indeterminate" : false} onCheckedChange={toggleAll} data-testid="select-all" aria-label="Select all" /></th>
                )}
                {cfg.columns.map((c) => (
                  <th key={c.key} className="px-4 py-3 text-left cc-overline whitespace-nowrap">{c.label}</th>
                ))}
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {showSelect && <td className="px-4"></td>}
                    {cfg.columns.map((c) => <td key={c.key} className="px-4 py-3.5"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}
                    <td></td>
                  </tr>
                ))}
              {!loading && rows.map((row) => (
                <tr key={row.id} onClick={() => onRowClick(row)} className={`group border-b border-slate-100 last:border-0 hover:bg-[#00C9E0]/[0.04] cursor-pointer transition-colors ${selected.has(row.id) ? "bg-[#00C9E0]/[0.06]" : ""}`} data-testid={`row-${row.id}`}>
                  {showSelect && (
                    <td className="w-10 px-4" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggleOne(row.id)} data-testid={`select-${row.id}`} aria-label="Select row" /></td>
                  )}
                  {cfg.columns.map((c) => (
                    <td key={c.key} className="px-4 py-3.5 whitespace-nowrap">{renderCell(c, row)}</td>
                  ))}
                  <td className="px-2" onClick={(e) => e.stopPropagation()}>{rowMenu(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length === 0 && emptyBlock}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && [...Array(5)].map((_, i) => (
          <div key={i} className="cc-card p-4"><div className="h-5 w-1/2 bg-slate-100 rounded animate-pulse mb-3" /><div className="h-4 w-3/4 bg-slate-100 rounded animate-pulse" /></div>
        ))}
        {!loading && rows.length === 0 && <div className="cc-card">{emptyBlock}</div>}
        {!loading && rows.map((row) => {
          const [first, ...rest] = cfg.columns;
          return (
            <div key={row.id} onClick={() => onRowClick(row)} className="cc-card cc-card-hover p-4 cursor-pointer" data-testid={`row-${row.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {showSelect && <div onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggleOne(row.id)} data-testid={`select-${row.id}`} /></div>}
                  <div className="font-semibold text-slate-800 min-w-0 truncate">{renderCell(first, row)}</div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>{rowMenu(row)}</div>
              </div>
              <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
                {rest.slice(0, 6).map((c) => (
                  <div key={c.key} className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</p>
                    <div className="text-sm truncate mt-0.5">{renderCell(c, row)}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <EntityDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        fields={cfg.fields}
        initial={dialog.initial}
        title={dialog.initial ? `Edit ${cfg.singular}` : `New ${cfg.singular}`}
        endpoint={dialog.initial ? `/${cfg.endpoint}/${dialog.initial.id}` : `/${cfg.createEndpoint || cfg.endpoint}`}
        method={dialog.initial ? "put" : "post"}
        optionSources={sources}
        duplicateCheck={cfg.duplicateCheck}
        onSaved={fetchRows}
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          {confirm && (confirm.mode === "purge" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirm.ids.length === 1 ? `Delete this ${cfg.singular.toLowerCase()} permanently?` : `Permanently delete ${confirm.ids.length} ${cfg.title.toLowerCase()}?`}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirm.label ? <span className="font-semibold text-slate-700">{confirm.label}<br /></span> : null}
                  {confirm.ids.length === 1 ? "This record will be" : "These records will be"} permanently removed from the CRM. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={runConfirm} className="bg-[#C42607] hover:bg-[#9e1e06]" data-testid="confirm-delete">
                  {confirm.ids.length === 1 ? "Delete Permanently" : `Delete ${confirm.ids.length} Permanently`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirm.ids.length === 1 ? `Archive this ${cfg.singular.toLowerCase()}?` : `Archive ${confirm.ids.length} ${cfg.title.toLowerCase()}?`}
                </AlertDialogTitle>
                <AlertDialogDescription>They will be hidden from active lists but preserved in history and restorable by an admin.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={runConfirm} className="bg-[#FE421C] hover:bg-[#C42607]" data-testid="confirm-archive">
                  {confirm.ids.length === 1 ? "Archive" : `Archive ${confirm.ids.length}`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ))}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
